import { API_URLS } from '../config/api';
import {
  Meeting,
  MeetingStatus,
  CreateMeetingRequest,
  UpdateMeetingRequest,
  UpdateSummaryRequest,
  AgendaItem,
  MeetingChatResponse,
  OutcomesConflictError,
  PersonaBriefing,
  PrepComment,
  CreatePrepCommentRequest,
  PrepAction,
  PrepActionsResponse,
  CreatePrepActionRequest
} from '../types/meeting';
import { AuthUser } from '../types/auth';

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const errorText = await response.text();
    let message = `Request failed: ${response.status}`;
    try {
      const parsed = JSON.parse(errorText);
      message = parsed.error || parsed.detail || parsed.title || message;
    } catch {
      if (errorText) message = errorText;
    }
    throw new Error(message);
  }
  if (response.status === 204) return undefined as T;
  return response.json();
}

const jsonHeaders = { 'Content-Type': 'application/json' };

class MeetingsAPIService {
  async listMeetings(options: { status?: MeetingStatus; limit?: number; offset?: number } = {}): Promise<Meeting[]> {
    const searchParams = new URLSearchParams();
    if (options.status) searchParams.append('status', options.status);
    if (options.limit !== undefined) searchParams.append('limit', options.limit.toString());
    if (options.offset !== undefined) searchParams.append('offset', options.offset.toString());

    const url = `${API_URLS.MEETINGS}${searchParams.toString() ? '?' + searchParams.toString() : ''}`;
    const response = await fetch(url, { method: 'GET', headers: jsonHeaders });
    const meetings = await handleResponse<Meeting[]>(response);
    return [...meetings].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
  }

  async getMeeting(meetingId: string): Promise<Meeting> {
    const response = await fetch(API_URLS.getMeetingUrl(meetingId), { method: 'GET', headers: jsonHeaders });
    return handleResponse<Meeting>(response);
  }

  // List all users so a Secretary can link an attendee to a real account
  // (Secretary/Admin only server-side). Returns [] if the caller isn't allowed
  // so the attendee form degrades to manual entry rather than erroring.
  async listUsers(): Promise<AuthUser[]> {
    try {
      const response = await fetch(API_URLS.USERS, { method: 'GET', headers: jsonHeaders });
      if (!response.ok) return [];
      return (await response.json()) as AuthUser[];
    } catch {
      return [];
    }
  }

  async createMeeting(request: CreateMeetingRequest): Promise<Meeting> {
    const response = await fetch(API_URLS.MEETINGS, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(request)
    });
    return handleResponse<Meeting>(response);
  }

  async updateMeeting(meetingId: string, request: UpdateMeetingRequest): Promise<Meeting> {
    const response = await fetch(API_URLS.getMeetingUrl(meetingId), {
      method: 'PUT',
      headers: jsonHeaders,
      body: JSON.stringify(request)
    });
    return handleResponse<Meeting>(response);
  }

  async deleteMeeting(meetingId: string): Promise<void> {
    const response = await fetch(API_URLS.getMeetingUrl(meetingId), { method: 'DELETE', headers: jsonHeaders });
    await handleResponse<void>(response);
  }

  async updateAgenda(meetingId: string, items: AgendaItem[]): Promise<Meeting> {
    const response = await fetch(API_URLS.getMeetingAgendaUrl(meetingId), {
      method: 'PUT',
      headers: jsonHeaders,
      body: JSON.stringify({ items })
    });
    return handleResponse<Meeting>(response);
  }

  async generateAgenda(meetingId: string, focus?: string): Promise<Meeting> {
    const response = await fetch(API_URLS.getMeetingAgendaGenerateUrl(meetingId), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ focus: focus || null })
    });
    return handleResponse<Meeting>(response);
  }

  async generateAttendees(meetingId: string, count: number = 4, guidance?: string): Promise<Meeting> {
    const response = await fetch(API_URLS.getMeetingAttendeesGenerateUrl(meetingId), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ count, guidance: guidance || null })
    });
    return handleResponse<Meeting>(response);
  }

  async generateBriefing(meetingId: string, guidance?: string): Promise<Meeting> {
    const response = await fetch(API_URLS.getMeetingBriefingGenerateUrl(meetingId), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ guidance: guidance || null })
    });
    return handleResponse<Meeting>(response);
  }

  // Fetch the calling user's custom-instructions-framed briefing, or null if none
  // has been generated yet.
  async getPersonaBriefing(meetingId: string): Promise<PersonaBriefing | null> {
    const response = await fetch(API_URLS.getMeetingPersonaBriefingUrl(meetingId), {
      method: 'GET',
      headers: jsonHeaders
    });
    const { briefing } = await handleResponse<{ briefing: PersonaBriefing | null }>(response);
    return briefing ?? null;
  }

  // Generate (or regenerate) the calling user's persona briefing, layering the
  // optional per-briefing steer on top of their profile custom instructions.
  async generatePersonaBriefing(meetingId: string, steer?: string): Promise<PersonaBriefing | null> {
    const response = await fetch(API_URLS.getMeetingPersonaBriefingUrl(meetingId), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ steer: steer || null })
    });
    const { briefing } = await handleResponse<{ briefing: PersonaBriefing | null }>(response);
    return briefing ?? null;
  }

  async uploadTranscript(meetingId: string, transcriptText: string): Promise<Meeting> {
    const response = await fetch(API_URLS.getMeetingTranscriptUrl(meetingId), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ transcript_text: transcriptText })
    });
    return handleResponse<Meeting>(response);
  }

  async generateSummary(meetingId: string): Promise<Meeting> {
    const response = await fetch(API_URLS.getMeetingSummaryGenerateUrl(meetingId), {
      method: 'POST',
      headers: jsonHeaders
    });
    return handleResponse<Meeting>(response);
  }

  // Full-replace edit of the meeting summary (secretary/admin only; members get
  // 403). Editing an approved summary resets approval on the backend.
  //
  // The edit is guarded by If-Match against the meeting's _etag: if someone else
  // saved first the server answers 412 and nothing is written, which surfaces as
  // an OutcomesConflictError so the caller can warn rather than lose an edit.
  async updateSummary(meetingId: string, request: UpdateSummaryRequest, etag?: string | null): Promise<Meeting> {
    const response = await fetch(API_URLS.getMeetingSummaryUrl(meetingId), {
      method: 'PUT',
      headers: etag ? { ...jsonHeaders, 'If-Match': etag } : jsonHeaders,
      body: JSON.stringify(request)
    });

    if (response.status === 412 || response.status === 428) {
      const body = await response.json().catch(() => null);
      throw new OutcomesConflictError(
        body?.error || 'These outcomes were changed by someone else since you started editing.',
        body?.meeting ?? null
      );
    }

    return handleResponse<Meeting>(response);
  }

  async sendChatMessage(meetingId: string, query: string): Promise<MeetingChatResponse> {
    const response = await fetch(API_URLS.getMeetingChatUrl(meetingId), {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify({ query })
    });
    return handleResponse<MeetingChatResponse>(response);
  }

  // Meeting materials reuse the file pipeline with the meeting id in project_id
  async listFiles(meetingId: string): Promise<any[]> {
    const url = `${API_URLS.FILES}?scope=PROJECT&project_id=${encodeURIComponent(meetingId)}&limit=100`;
    const response = await fetch(url, { method: 'GET', headers: jsonHeaders });
    return handleResponse<any[]>(response);
  }

  async uploadFile(meetingId: string, file: File): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('scope', 'PROJECT');
    formData.append('project_id', meetingId);
    formData.append('process_immediately', 'true');

    const response = await fetch(API_URLS.FILE_UPLOAD, { method: 'POST', body: formData });
    return handleResponse<any>(response);
  }

  async deleteFile(fileId: string): Promise<void> {
    const url = `${API_URLS.FILES}${encodeURIComponent(fileId)}`;
    const response = await fetch(url, { method: 'DELETE' });
    await handleResponse<void>(response);
  }

  getFileDownloadUrl(fileId: string): string {
    return `${API_URLS.FILES}${encodeURIComponent(fileId)}/download`;
  }

  // Download the original file content as a Blob. This must go through fetch (not
  // a native anchor click) so the global auth interceptor attaches the Bearer
  // token — the download endpoint requires auth, and an unauthenticated anchor
  // navigation gets a 401 that serves the SPA's index.html instead of the file.
  async downloadFile(fileId: string): Promise<Blob> {
    const response = await fetch(this.getFileDownloadUrl(fileId), { method: 'GET' });
    if (!response.ok) {
      const message = `Failed to download file: ${response.status}`;
      throw new Error(message);
    }
    return response.blob();
  }

  // What the signed-in user has been asked to do at this meeting, plus what is
  // still outstanding from earlier meetings of the same committee.
  async getPrepActions(meetingId: string): Promise<PrepActionsResponse> {
    const url = `${API_URLS.getMeetingUrl(meetingId)}/prep-actions`;
    const response = await fetch(url, { method: 'GET', headers: jsonHeaders });
    return handleResponse<PrepActionsResponse>(response);
  }

  // Secretary-only: assign a prep task to a linked attendee of the meeting.
  async createPrepAction(meetingId: string, request: CreatePrepActionRequest): Promise<PrepAction> {
    const url = `${API_URLS.getMeetingUrl(meetingId)}/prep-actions`;
    const response = await fetch(url, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(request)
    });
    return handleResponse<PrepAction>(response);
  }

  // Open or complete a prep task. Permitted to its assignee as well as a
  // secretary — the server decides, since only it knows who it belongs to.
  async setPrepActionStatus(meetingId: string, prepActionId: string, status: 'open' | 'completed'): Promise<PrepAction> {
    const url = `${API_URLS.getMeetingUrl(meetingId)}/prep-actions/${encodeURIComponent(prepActionId)}`;
    const response = await fetch(url, {
      method: 'PATCH',
      headers: jsonHeaders,
      body: JSON.stringify({ status })
    });
    return handleResponse<PrepAction>(response);
  }

  async deletePrepAction(meetingId: string, prepActionId: string): Promise<void> {
    const url = `${API_URLS.getMeetingUrl(meetingId)}/prep-actions/${encodeURIComponent(prepActionId)}`;
    const response = await fetch(url, { method: 'DELETE', headers: jsonHeaders });
    await handleResponse<void>(response);
  }

  // Prep comments: the signed-in user's private preparation notes against this
  // meeting's materials. The server scopes every read to the caller, so these
  // never return another user's notes and the UI needs no ownership filtering.
  async listPrepComments(meetingId: string, fileId?: string): Promise<PrepComment[]> {
    const query = fileId ? `?file_id=${encodeURIComponent(fileId)}` : '';
    const url = `${API_URLS.getMeetingUrl(meetingId)}/prep-comments${query}`;
    const response = await fetch(url, { method: 'GET', headers: jsonHeaders });
    const payload = await handleResponse<{ comments: PrepComment[] }>(response);
    return payload.comments || [];
  }

  async createPrepComment(meetingId: string, request: CreatePrepCommentRequest): Promise<PrepComment> {
    const url = `${API_URLS.getMeetingUrl(meetingId)}/prep-comments`;
    const response = await fetch(url, {
      method: 'POST',
      headers: jsonHeaders,
      body: JSON.stringify(request)
    });
    return handleResponse<PrepComment>(response);
  }

  // Only the body is editable. Re-anchoring a note to another page would
  // silently change what it refers to, so the server rejects it.
  async updatePrepComment(meetingId: string, commentId: string, body: string): Promise<PrepComment> {
    const url = `${API_URLS.getMeetingUrl(meetingId)}/prep-comments/${encodeURIComponent(commentId)}`;
    const response = await fetch(url, {
      method: 'PATCH',
      headers: jsonHeaders,
      body: JSON.stringify({ body })
    });
    return handleResponse<PrepComment>(response);
  }

  async deletePrepComment(meetingId: string, commentId: string): Promise<void> {
    const url = `${API_URLS.getMeetingUrl(meetingId)}/prep-comments/${encodeURIComponent(commentId)}`;
    const response = await fetch(url, { method: 'DELETE' });
    await handleResponse<void>(response);
  }

  // Outcomes approval (secretary-only). Returns the updated meeting so the
  // caller can sync status (approving publishes the minutes -> Completed).
  // A 404 is surfaced as an error for the caller to degrade.
  //
  // Guarded by If-Match like updateSummary: approval signs off on a specific
  // version, so if the text changed since it was loaded the server answers 412
  // and nothing is approved, surfacing as an OutcomesConflictError.
  async approveOutcomes(meetingId: string, etag?: string | null): Promise<Meeting> {
    const response = await fetch(API_URLS.getMeetingOutcomesApproveUrl(meetingId), {
      method: 'POST',
      headers: etag ? { ...jsonHeaders, 'If-Match': etag } : jsonHeaders
    });

    if (response.status === 412 || response.status === 428) {
      const body = await response.json().catch(() => null);
      throw new OutcomesConflictError(
        body?.error || 'These outcomes were changed by someone else since you opened them.',
        body?.meeting ?? null
      );
    }

    return handleResponse<Meeting>(response);
  }

  // Export approved minutes as PDF or PPTX. Returns the raw Response so the
  // caller can distinguish 409 (unapproved) / 404 (endpoint absent) before
  // reading the body.
  async exportOutcomes(meetingId: string, format: 'pdf' | 'pptx'): Promise<Response> {
    const url =
      format === 'pdf'
        ? API_URLS.getMeetingOutcomesExportPdfUrl(meetingId)
        : API_URLS.getMeetingOutcomesExportPptxUrl(meetingId);
    return fetch(url, { method: 'POST', headers: jsonHeaders });
  }
}

export const meetingsAPI = new MeetingsAPIService();
export default meetingsAPI;
